import { LetterEditor } from '@/components/editor/letter-editor';
import { EnvelopeSuccess } from '@/components/envelope-success';
import {
  type PublicProfile,
  type UserProfile,
  formatUserDisplay,
  getDisplayName,
  getSignatureFontFamily,
  letters,
  users
} from '@/lib/api';
import { useDebounce } from '@/lib/hooks/use-debounce';
import { routes } from '@/lib/routes';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Loader2, MapPin, Save, Search, Send, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

export default function ComposePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const toUserId = searchParams.get('to');
  const replyToId = searchParams.get('replyTo');

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [recipient, setRecipient] = useState<PublicProfile | null>(null);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState<unknown>(null);
  const [draftId, setDraftId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PublicProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const [deliveryEstimate, setDeliveryEstimate] = useState<{
    delayDays: number;
    estimatedDelivery: string;
  } | null>(null);

  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sentSuccess, setSentSuccess] = useState<{
    recipientName: string;
    recipientCountry: string;
    senderName: string;
    deliveryDate: string;
  } | null>(null);

  // Editable signature for this letter
  const [signatureText, setSignatureText] = useState('');

  const debouncedSearch = useDebounce(searchQuery, 300);

  // Load user profile for signature
  useEffect(() => {
    users
      .getProfile()
      .then((p) => {
        setProfile(p);
        // Initialize signature from profile
        setSignatureText(p?.signatureText || '');
      })
      .catch(console.error);
  }, []);

  // Load recipient if provided
  useEffect(() => {
    if (toUserId) {
      users.getSuggested(50).then((results) => {
        const found = results.find((u) => u.id === toUserId);
        if (found) {
          setRecipient(found);
        }
      });
    }
  }, [toUserId]);

  // Search users
  useEffect(() => {
    if (!debouncedSearch) {
      setSearchResults([]);
      return;
    }

    const search = async () => {
      setSearching(true);
      try {
        const results = await users.search(debouncedSearch, 5);
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    search();
  }, [debouncedSearch]);

  // Get delivery estimate when recipient is selected
  useEffect(() => {
    if (!recipient) {
      setDeliveryEstimate(null);
      return;
    }

    letters
      .getDeliveryEstimate(recipient.id)
      .then(setDeliveryEstimate)
      .catch(() => {});
  }, [recipient]);

  const selectRecipient = (user: PublicProfile) => {
    setRecipient(user);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  const handleSaveDraft = async () => {
    if (!recipient) {
      setError(t('compose.selectRecipient'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (draftId) {
        await letters.updateDraft(draftId, {
          subject: subject || 'Untitled',
          content
        });
      } else {
        const result = await letters.createDraft({
          recipientId: recipient.id,
          subject: subject || 'Untitled',
          content,
          replyToId: replyToId || undefined
        });
        setDraftId(result.id);
      }
    } catch {
      setError(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!recipient) {
      setError(t('compose.selectRecipient'));
      return;
    }

    if (!subject.trim()) {
      setError(t('compose.subject'));
      return;
    }

    if (!content) {
      setError(t('compose.content'));
      return;
    }

    setSending(true);
    setError('');

    try {
      let letterId = draftId;

      if (!letterId) {
        const result = await letters.createDraft({
          recipientId: recipient.id,
          subject,
          content,
          replyToId: replyToId || undefined
        });
        letterId = result.id;
      } else {
        await letters.updateDraft(letterId, { subject, content });
      }

      const { deliveredAt } = await letters.send(letterId);

      setSentSuccess({
        recipientName: getDisplayName(recipient),
        recipientCountry: recipient.country,
        senderName: profile ? getDisplayName(profile) : 'You',
        deliveryDate: new Date(deliveredAt).toLocaleDateString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      });
    } catch {
      setError(t('common.error'));
    } finally {
      setSending(false);
    }
  };

  const handleWriteAnother = () => {
    setRecipient(null);
    setSubject('');
    setContent(null);
    setDraftId(null);
    setDeliveryEstimate(null);
    setSentSuccess(null);
    setError('');
    // Reset signature to profile default
    setSignatureText(profile?.signatureText || '');
  };

  // Success state - show envelope visualization
  if (sentSuccess) {
    return (
      <EnvelopeSuccess
        senderName={sentSuccess.senderName}
        recipientName={sentSuccess.recipientName}
        recipientCountry={sentSuccess.recipientCountry}
        deliveryDate={sentSuccess.deliveryDate}
        onWriteAnother={handleWriteAnother}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to={routes.inbox} className="p-2 rounded-full hover:bg-gray-warm/30 transition-all">
            <ArrowLeft className="w-5 h-5 text-sepia" />
          </Link>
          <h1 className="text-2xl font-serif text-ink">{t('compose.title')}</h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={saving || !recipient}
            className="flex items-center gap-2 px-4 py-2 text-sepia hover:bg-gray-warm/30 rounded-lg transition-all disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('compose.saveDraft')}
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !recipient || !subject || !content}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg font-medium hover:bg-gold/90 disabled:opacity-50 transition-all"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t('compose.send')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red/10 border border-red/20 text-red rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Recipient Selection - outside paper */}
      <div className="bg-white rounded-lg p-4 mb-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
        <span className="block text-sm font-medium text-sepia mb-2">{t('compose.to')}</span>

        {recipient ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-warm/50 flex items-center justify-center text-lg font-medium text-sepia">
                {getDisplayName(recipient).charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-ink">{getDisplayName(recipient)}</p>
                {recipient.displayName && (
                  <p className="text-xs text-sepia/60">@{recipient.username}</p>
                )}
                <p className="text-xs text-sepia/60 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {recipient.country}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {deliveryEstimate && (
                <div className="flex items-center gap-1 text-sm text-blue">
                  <Clock className="w-4 h-4" />
                  {t('compose.deliveryDays', { days: deliveryEstimate.delayDays })}
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  setRecipient(null);
                  setShowSearch(true);
                }}
                className="p-1 rounded hover:bg-gray-warm/30 transition-all"
              >
                <X className="w-4 h-4 text-sepia" />
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearch(true);
              }}
              onFocus={() => setShowSearch(true)}
              placeholder={t('compose.searchRecipient')}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
            />

            {/* Search results dropdown */}
            {showSearch && (searchResults.length > 0 || searching) && (
              <div
                className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-gray-warm/50 overflow-hidden z-10"
                style={{ boxShadow: 'var(--shadow-md)' }}
              >
                {searching ? (
                  <div className="p-4 text-center">
                    <Loader2 className="w-5 h-5 text-sepia animate-spin mx-auto" />
                  </div>
                ) : (
                  searchResults.map((user) => (
                    <button
                      type="button"
                      key={user.id}
                      onClick={() => selectRecipient(user)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-gray-warm/20 transition-all text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-warm/50 flex items-center justify-center text-sm font-medium text-sepia">
                        {getDisplayName(user).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-ink">{formatUserDisplay(user)}</p>
                        <p className="text-xs text-sepia/60">{user.country}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Paper-like Composition Area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-lg overflow-hidden"
        style={{
          background: 'linear-gradient(to bottom, #FEFDFB 0%, #FAF7F2 100%)',
          boxShadow: '0 8px 32px rgba(91, 75, 61, 0.12), inset 0 0 120px rgba(197, 165, 114, 0.03)',
          border: '1px solid rgba(92, 75, 61, 0.1)'
        }}
      >
        {/* Paper texture overlay */}
        <div className="paper-texture absolute inset-0 pointer-events-none" />

        {/* Subject - elegant header style */}
        <div className="px-8 py-6 sm:px-12 sm:py-8 border-b border-gray-warm/30">
          <label
            htmlFor="subject-input"
            className="block text-xs uppercase tracking-widest text-sepia/60 mb-2"
          >
            {t('compose.subject')}
          </label>
          <input
            id="subject-input"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('compose.subjectPlaceholder')}
            maxLength={200}
            className="w-full text-xl font-serif text-ink placeholder:text-gray-warm/60 bg-transparent focus:outline-none"
          />
        </div>

        {/* Content - serif typography */}
        <div className="px-8 py-6 sm:px-12 sm:py-8 min-h-[400px]">
          <LetterEditor
            onChange={setContent}
            placeholder={t('compose.contentPlaceholder')}
            maxLength={10000}
            borderless
            fontFamily={getSignatureFontFamily(profile?.letterFont || 'CAVEAT')}
          />
        </div>

        {/* Editable Signature */}
        <div className="px-8 pb-8 sm:px-12 sm:pb-12">
          <div className="border-t border-gray-warm/30 pt-6">
            <p className="text-xs text-sepia/50 mb-2">{t('compose.yourSignature')}</p>
            <input
              type="text"
              value={signatureText}
              onChange={(e) => setSignatureText(e.target.value)}
              placeholder={t('profile.signaturePlaceholder')}
              maxLength={100}
              className="w-full text-xl text-blue text-right bg-transparent focus:outline-none placeholder:text-gray-warm/40"
              style={{ fontFamily: getSignatureFontFamily(profile?.signatureFont || 'CAVEAT') }}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
